import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Loading from '../../../LoadingComponent/LoadingComponent';
import * as Styled from '../SimplrOrder.styles';
import isEmpty from 'lodash/isEmpty';
import * as errorUtils from '../../../../util/error';
import { getGeoDataByType, GeoTypes, CODE_US } from '../../../../constants/geodata';
import { oneBtnPopupCreator, twoBtnPopupCreator } from '../../../popups/ConfirmPopup/helper';
import ImageIcon from '../../../ImageIcon/ImageIcon';
import { bindActionCreators } from 'redux';
import { updateShipmentAddress } from '../../../../actions/tools/SimplrOrderActions';
import { connect } from 'react-redux';
import { addNoteToSelectedTicket } from '../../../../actions';
import { extractSelectedTicketFromState, getSelectedOrderFromOrdersSearch } from '../../../../extractFromState';
import get from 'lodash/get';
import ChangeAddressForm from '../../common/ChangeAddressForm/ChangeAddressForm';
import { showErrorPopup } from '../../../popups/ErrorPopup/ErrorPopup';
import { StepTitle, Subtext } from './SimplrUpdateShipmentAddress.styles';
import { handleError } from 'services/ErrorService/ErrorService';

export class SimplrUpdateShipmentAddress extends Component {
  static propTypes = {
    updateShipmentAddress: PropTypes.func,
  };

  static defaultProps = {
    updateShipmentAddress: () => Promise.resolve(),
  };

  LOADER_LABELS_MAP = {
    isUpdateAddressRequesting: 'Updating address...',
  };

  formFields = [
    {
      field: 'first_name',
      label: 'First name',
      type: 'input',
    },
    {
      field: 'last_name',
      label: 'Last name',
      type: 'input',
    },
    {
      field: 'country',
      label: 'Country',
      type: 'select',
      items: getGeoDataByType(GeoTypes.COUNTRY_LIST).map((item) => ({ value: item.code, label: item.name })),
      cleanBefore: ['province', 'province_code'],
    },
    {
      field: 'city',
      label: 'City',
      type: 'input',
    },
    {
      field: 'province',
      label: 'State/Region',
      type: 'select',
      items: getGeoDataByType(GeoTypes.PROVINCE_LIST).map((item) => ({ value: item.code, label: item.name })),
      show: { country: 'US' },
    },
    {
      field: 'province',
      label: 'Province',
      type: 'input',
      hide: { country: 'US' },
    },
    {
      field: 'address1',
      label: 'Address line 1',
      type: 'input',
    },
    {
      field: 'address2',
      label: 'Address line 2',
      type: 'input',
    },
    {
      field: 'zip',
      label: 'ZIP',
      type: 'input',
    },
    {
      field: 'phone',
      label: 'Phone',
      type: 'input',
    },
  ];

  getNoteValueWithName = (value, displayName) => (value ? `${displayName}: ${value}\n` : '');

  get addNoteMessage() {
    const address = this.getUpdateAddressPayload() || {};

    return [
      `Action: Change Delivery Address, updated address:`,
      this.getNoteValueWithName(address.first_name, 'First Name'),
      this.getNoteValueWithName(address.last_name, 'Last Name'),
      this.getNoteValueWithName(address.company, 'Company'),
      this.getNoteValueWithName(address.phone, 'Phone Number'),
      this.getNoteValueWithName(address.country, 'Country'),
      this.getNoteValueWithName(address.city, 'City'),
      `${address.country === CODE_US ? 'State' : 'Province'}: ${address.province || 'Not Available'}`,
      this.getNoteValueWithName(address.address1, 'Address line 1'),
      this.getNoteValueWithName(address.address2, 'Address line 2'),
    ]
      .filter((item) => item !== '')
      .join('\n');
  }

  _isMounted = false;

  state = {
    address: {},
    errorMessages: {},
    isUpdateAddressRequesting: false,
    addressId: '',
    apiErrorMessage: '',
  };

  /**
   * Find active requesting state property name
   * @return {string|null}
   */
  requestingStatePropertyName() {
    return ['isUpdateAddressRequesting'].find((key) => this.state[key]);
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  requestWrapper = async ({ request, success = () => {}, error = () => {}, processingState }) => {
    this.setState({ apiErrorMessage: '' });
    if (typeof processingState === 'string') {
      this.setState({ [processingState]: true });
    }

    try {
      const response = await request();
      if (response.success) {
        success();
      } else {
        error(response.errorMessage, 'Oops!');
      }
    } catch (err) {
      handleError({ error: err });
      error(`There was a problem with updating the address`, 'Oops!');
    }

    if (typeof processingState === 'string' && this._isMounted) {
      this.setState({ [processingState]: false });
    }
  };

  getAddresses() {
    const addresses = [this.props.selectedOrder.billingAddress];
    this.props.selectedOrder.shipment.map((shipment) => addresses.push(shipment.shippingAddress));

    return addresses;
  }

  renderAddress(address) {
    if (address) {
      const addressDetails = [
        'addressType',
        'firstName',
        'address1',
        'address2',
        'suite',
        'city',
        'state',
        'zip',
        'country',
        'phone',
      ];
      const validAddress = [];
      addressDetails.forEach((item) => {
        if (!!address[item]) {
          item === 'firstName'
            ? validAddress.push(address[item] + ' ' + address['lastName'])
            : validAddress.push(address[item]);
        }
      });

      return validAddress.join(', ');
    }
  }

  renderRadioButton() {
    return (
      <Styled.RadioButtons>
        {this.getAddresses().map((address) => (
          <label key={'radio-' + address.addressId}>
            <Styled.RadioButtonInput
              type="radio"
              value={address.addressId}
              checked={this.state.addressId === address.addressId}
              onChange={this.handleRadioButtonChange}
            />
            {this.renderAddress(address)}
          </label>
        ))}
      </Styled.RadioButtons>
    );
  }

  handleRadioButtonChange = (changeEvent) => {
    this.setState({ addressId: changeEvent.target.value });
  };

  extractAddressPayloadFromState() {
    return this.formFields.reduce(
      (address, option) => ({
        ...address,
        [option.field]: this.state.address[option.field],
      }),
      {}
    );
  }

  getUpdateAddressPayload = () => this.extractAddressPayloadFromState();

  validate = (value) => {
    const errorMessages = {};
    if (!value.last_name) {
      errorMessages.last_name = 'Last Name is required';
    }

    if (!value.address1) {
      errorMessages.address1 = 'Address line 1 is required';
    }

    if (!value.city) {
      errorMessages.city = 'City is required';
    }

    if (!value.country) {
      errorMessages.country = 'Country is required';
    }

    if (!value.province) {
      errorMessages.province = 'Province / State is required';
    }

    if (!value.zip) {
      errorMessages.zip = 'Zip is required';
    }

    this.setState({ errorMessages });

    return Object.keys(errorMessages).length === 0;
  };

  handleUpdateAddress = async () => {
    if (!this.validate(this.state.address)) {
      return null;
    }
    const onPopupConfirm = () => {
      return this.requestWrapper({
        request: this.updateShipmentAddressRequest,
        success: this.successUpdateAddressHandler,
        error: showErrorPopup,
        stateProp: 'isUpdateAddressRequesting',
      });
    };

    twoBtnPopupCreator({
      content: {
        main: 'You are about update an address. Are you sure about your selection?',
        iconPath: '/images/green-alert-bell.svg',
      },
      btnConfig: {
        dismiss: 'DISMISS',
        confirm: 'UPDATE ADDRESS(ES)',
      },
      onConfirm: onPopupConfirm,
    });
  };

  successUpdateAddressHandler = async () => {
    oneBtnPopupCreator({
      content: {
        main: 'Address(es) has been updated successfully',
        iconPath: ImageIcon.type.checkmarkGreen,
      },
      btnConfig: 'CLOSE',
    });
    await this.props.addNoteToSelectedTicket(this.addNoteMessage);
  };

  extractRequestApiErrors(error) {
    try {
      const message = get(error, 'response.body.message', '{}');
      const { zipcode: zip, address1, state: province, city } = JSON.parse(message);

      return { zip, address1, province, city };
    } catch (_) {}
  }

  extractApiErrors(error) {
    const errorMessages = {
      ...errorUtils.getApiValidationErrorMessage(error),
      ...this.extractRequestApiErrors(error),
    };
    for (let key in errorMessages) {
      if (typeof errorMessages[key] !== 'string') {
        delete errorMessages[key];

        break;
      }

      // Specific error case for ZIP code
      const err = errorMessages[key];
      const pattern = new RegExp(' zip ', 'ig');

      if (pattern.test(err)) {
        errorMessages[key] = errorMessages[key].replace(pattern, ' ZIP ');
      }

      if (err[err.length - 1] !== '.') {
        errorMessages[key] = `${errorMessages[key]}.`;
      }
    }

    return errorMessages;
  }

  commonErrorHandler = (error) => {
    const errorMessages = this.extractApiErrors(error);
    if (isEmpty(errorMessages)) {
      this.setState({ apiErrorMessage: 'Something went wrong! Request failed.' });

      return;
    }
    this.setState({ errorMessages });
  };

  updateShipmentAddressRequest = async () => {
    const { simplrAccountPtrId } = this.props.selectedTicket;
    const orderId = this.props.selectedOrder.orderId;
    const payload = {
      orderId,
      address: this.getUpdateAddressPayload(),
      addressId: this.state.addressId,
      addressType: this.getAddresses().find((address) => address.addressId === this.state.addressId).addressType,
      domain: this.props.domain,
    };

    return await this.props.updateShipmentAddress(simplrAccountPtrId, payload);
  };

  renderButton() {
    return (
      <Styled.ButtonWrapper>
        <Styled.PrimaryButton onClick={this.handleUpdateAddress} type="button">
          UPDATE
        </Styled.PrimaryButton>
      </Styled.ButtonWrapper>
    );
  }

  update = (data) => {
    this.setState({ address: { ...this.state.address, ...data } });
    const errorMessages = { ...this.state.errorMessages };
    Object.keys(data).forEach((key) => delete errorMessages[key]);
    this.setState({ errorMessages });
  };

  render() {
    if (this.requestingStatePropertyName()) {
      return <Loading delay={50} label={this.LOADER_LABELS_MAP[this.requestingStatePropertyName()] || 'Loading...'} />;
    }

    return (
      <Styled.AddressWrapper className="fs-block">
        <Styled.OrderTaskTitle>Update Address</Styled.OrderTaskTitle>
        <StepTitle>
          <Subtext>Step 1 - </Subtext>Select a address to be updated
        </StepTitle>
        <Styled.RadioButtonWrap>{this.renderRadioButton()}</Styled.RadioButtonWrap>

        <StepTitle>
          <Subtext>Step 2 - </Subtext>New address detail
        </StepTitle>
        <form>
          <ChangeAddressForm
            errors={this.state.errorMessages}
            readOnly={false}
            formFields={this.formFields}
            update={this.update}
            data={this.state.address}
          />
        </form>
        {this.renderButton()}
      </Styled.AddressWrapper>
    );
  }
}

const mapStateToProps = (state) => {
  const selectedOrder = getSelectedOrderFromOrdersSearch(state);

  return {
    selectedOrder: selectedOrder,
    selectedTicket: extractSelectedTicketFromState(state),
    domain: selectedOrder.domain || null,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateShipmentAddress: updateShipmentAddress,
      addNoteToSelectedTicket,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(SimplrUpdateShipmentAddress);
